/**
 * T115-F4-c: Reusable tag input component
 *
 * Displays existing tags as removable badges with a free-text input
 * for adding new tags. Supports autocomplete suggestions, max count,
 * max length, and a compact mode for inline use.
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  tagStats?: { tag: string; count: number }[];
  maxTags?: number;
  maxLength?: number;
  placeholder?: string;
  compact?: boolean;
  disabled?: boolean;
  className?: string;
}

export function TagInput({
  value,
  onChange,
  suggestions = [],
  tagStats,
  maxTags = 10,
  maxLength = 30,
  placeholder = 'Add tags...',
  compact = false,
  disabled = false,
  className,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedTagKeys = useMemo(
    () => new Set(value.map((tag) => tag.toLowerCase())),
    [value],
  );

  const tagCountByKey = useMemo(() => {
    const counts = new Map<string, number>();
    tagStats?.forEach((stat) => {
      const key = stat.tag.trim().toLowerCase();
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + stat.count);
    });
    return counts;
  }, [tagStats]);

  const filteredSuggestions = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    const seen = new Set<string>();
    return suggestions
      .map((suggestion) => suggestion.trim())
      .filter((suggestion) => {
        if (!suggestion) return false;
        const key = suggestion.toLowerCase();
        if (seen.has(key) || selectedTagKeys.has(key)) return false;
        if (query && !key.includes(query)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.localeCompare(b));
  }, [inputValue, selectedTagKeys, suggestions]);

  const toTestId = (tag: string) => tag.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const addTag = useCallback(
    (raw: string) => {
      const tag = raw.trim();
      if (!tag) return;
      if (tag.length > maxLength) return;
      if (value.length >= maxTags) return;
      if (selectedTagKeys.has(tag.toLowerCase())) return;
      onChange([...value, tag]);
      setInputValue('');
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    },
    [value, onChange, maxTags, maxLength, selectedTagKeys],
  );

  const removeTag = useCallback(
    (index: number) => {
      const next = [...value];
      next.splice(index, 1);
      onChange(next);
    },
    [value, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
        addTag(filteredSuggestions[highlightedIndex]);
      } else {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value.length - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlightedIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Handle comma-separated paste/type
    if (val.includes(',')) {
      const parts = val.split(',');
      parts.forEach((part, i) => {
        if (i < parts.length - 1) {
          addTag(part);
        } else {
          setInputValue(part);
        }
      });
    } else {
      setInputValue(val.slice(0, maxLength));
    }
    setShowSuggestions(true);
    setHighlightedIndex(-1);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const atLimit = value.length >= maxTags;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          compact && 'px-2 py-1.5 gap-1',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {value.map((tag, i) => {
          const slug = toTestId(tag);
          const count = tagCountByKey.get(tag.toLowerCase());
          const showCount = count !== undefined && count > 0;
          return (
            <Badge
              key={`${tag}-${i}`}
              data-testid={`tag-chip-${slug}`}
              variant="secondary"
              className={cn(
                'gap-1 pr-1',
                compact && 'text-[10px] px-1.5 py-0',
              )}
            >
              {tag}
              {showCount && (
                <Badge
                  variant="secondary"
                  data-testid="tag-chip-count-badge"
                  className="text-[10px] px-1.5 py-0 h-4 shrink-0 font-normal tabular-nums bg-muted-foreground/20 text-secondary-foreground/80"
                >
                  {count}
                </Badge>
              )}
              {!disabled && (
                <button
                  type="button"
                  className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 min-h-[22px] min-w-[22px] flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(i);
                  }}
                  aria-label={`Remove tag ${tag}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          );
        })}
        {!atLimit && !disabled && (
          <Input
            ref={inputRef}
            data-testid="tag-input-field"
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            placeholder={value.length === 0 ? placeholder : ''}
            className={cn(
              'flex-1 min-w-0 border-0 p-0 h-auto shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
              compact && 'text-xs',
            )}
            disabled={disabled}
          />
        )}
      </div>

      {/* Autocomplete dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && !disabled && (
        <div className="absolute inset-x-0 z-50 mt-1 w-full min-w-0 max-h-64 overflow-y-auto rounded-md border bg-popover p-2 shadow-md" data-testid="tag-suggestions">
          <div className="flex flex-wrap gap-1.5">
            {filteredSuggestions.map((suggestion, i) => {
              const count = tagCountByKey.get(suggestion.toLowerCase()) ?? 0;
              return (
                <button
                  key={suggestion}
                  type="button"
                  data-testid={`tag-suggestion-${toTestId(suggestion)}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm cursor-pointer border border-border/50 transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    i === highlightedIndex && 'bg-accent text-accent-foreground',
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addTag(suggestion);
                  }}
                  onMouseEnter={() => setHighlightedIndex(i)}
                >
                  <span className="truncate max-w-[150px]">{suggestion}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0 font-normal tabular-nums">
                    {count}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Limit indicator */}
      {atLimit && (
        <p className="text-[10px] text-muted-foreground mt-1">
          Maximum {maxTags} tags reached
        </p>
      )}
    </div>
  );
}
